<template>
  <main class="notice-page">
    <router-link class="back-link" to="/">← Home</router-link>

    <header class="page-header">
      <div>
        <h1>Notice</h1>
        <p>공지사항과 첨부파일을 관리합니다.</p>
      </div>
      <button type="button" class="primary-button" :disabled="saving" @click="openAddDialog">Add Notice</button>
    </header>

    <section class="notice-panel" aria-labelledby="notice-list-title">
      <div class="panel-header">
        <h2 id="notice-list-title">Notice List</h2>
        <span>{{ notices.length }}건</span>
      </div>
      <div v-if="listLoading" class="empty-state">공지사항을 불러오는 중입니다.</div>
      <div v-else-if="listError" class="empty-state" role="alert">
        {{ listError }} <button type="button" class="retry-button" @click="loadNotices">다시 시도</button>
      </div>
      <div v-else-if="notices.length === 0" class="empty-state">
        등록된 공지가 없습니다. Add Notice를 눌러 공지를 작성해 보세요.
      </div>
      <button
        v-for="notice in notices"
        :key="notice.id"
        type="button"
        class="notice-item"
        @click="notice.cleanupPending ? retryDelete(notice) : openEditDialog(notice)"
      >
        <span class="notice-item-title">{{ notice.title }}</span>
        <span class="notice-item-body">{{ notice.body }}</span>
        <span v-if="notice.cleanupPending" class="notice-item-meta form-error">Azure 파일 삭제가 완료되지 않았습니다. 클릭해서 다시 시도하세요.</span>
        <span v-else class="notice-item-meta">수신자 {{ recipientLabel(notice.selectedCompanies) }} · 첨부파일 {{ notice.files.length }}개 · {{ formatDate(notice.registeredAt) }}</span>
      </button>
    </section>

    <p v-if="saveWarning" class="form-error" role="alert">{{ saveWarning }}</p>

    <div v-if="isDialogOpen" class="dialog-overlay" @click.self="closeDialog">
      <section class="notice-dialog" role="dialog" aria-modal="true" aria-labelledby="notice-dialog-title">
        <div class="dialog-header">
          <h2 id="notice-dialog-title">{{ editingId === null ? 'Add Notice' : 'Edit Notice' }}</h2>
          <button type="button" class="close-button" aria-label="닫기" :disabled="saving" @click="closeDialog">×</button>
        </div>

        <form @submit.prevent="saveNotice">
          <label class="field-label" for="notice-companies">협력업체 선택</label>
          <button
            id="notice-companies"
            type="button"
            class="company-select-button"
            :disabled="companyLoading || companyOptions.length === 0"
            @click="openCompanyDialog"
          >
            {{ selectedCompanyLabel }}
          </button>
          <p v-if="companyLoading" class="file-help" role="status">협력업체 목록을 불러오는 중입니다.</p>
          <p v-else-if="companyError" class="form-error" role="alert">
            {{ companyError }}
            <button type="button" class="retry-button" @click="loadCompanyOptions">다시 시도</button>
          </p>
          <p v-else class="file-help">수신할 협력업체를 선택하세요.</p>

          <div class="notice-content-fields">
            <div class="notice-content-field">
              <label class="field-label" for="notice-title">제목</label>
              <input id="notice-title" v-model="form.title" class="text-input" type="text" required />
            </div>
            <div class="notice-content-field">
              <label class="field-label" for="notice-body">본문</label>
              <textarea id="notice-body" v-model="form.body" class="text-input body-input" required></textarea>
            </div>
          </div>

          <div class="attachments-heading">
            <label class="field-label" for="notice-files">Attachments</label>
            <span>{{ form.files.length }}/3</span>
          </div>
          <p class="file-help">엑셀, Word, PPT, PDF · 최대 3개 · 파일당 20MB 이하</p>
          <input
            id="notice-files"
            class="file-input"
            type="file"
            multiple
            accept=".xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf"
            :disabled="saving || form.files.length >= maxFiles"
            @change="onFilesSelected"
          />

          <ul v-if="form.files.length" class="file-list">
            <li v-for="(file, index) in form.files" :key="file.blobName || file.name + '-' + file.lastModified + '-' + index">
              <span class="file-name">
                {{ file.name }} <small v-if="file.size">({{ formatFileSize(file.size) }})</small>
                <a v-if="file.blobName" :href="attachmentUrl(file)">다운로드</a>
              </span>
              <button type="button" class="remove-file-button" :disabled="saving" :aria-label="file.name + ' 제거'" @click="removeFile(index)">제거</button>
            </li>
          </ul>

          <p v-if="formError" class="form-error" role="alert">{{ formError }}</p>

          <div class="dialog-actions">
            <button v-if="editingId !== null" type="button" class="delete-button" :disabled="saving" @click="deleteNotice">Delete</button>
            <span class="action-spacer"></span>
            <button type="button" class="secondary-button" :disabled="saving" @click="closeDialog">Cancel</button>
            <button type="submit" class="primary-button" :disabled="saving">{{ saving ? '저장 중...' : editingId === null ? 'Add' : 'Save' }}</button>
          </div>
        </form>
      </section>
    </div>

    <div v-if="isCompanyDialogOpen" class="dialog-overlay company-overlay" @click.self="closeCompanyDialog">
      <section class="company-dialog" role="dialog" aria-modal="true" aria-labelledby="company-dialog-title">
        <div class="dialog-header">
          <h2 id="company-dialog-title">협력업체 선택</h2>
          <button type="button" class="close-button" aria-label="닫기" @click="closeCompanyDialog">×</button>
        </div>
        <p class="preview-note">업체 코드 또는 회사명으로 검색한 뒤 수신할 업체를 선택하세요.</p>
        <input v-model="companySearch" class="text-input company-search" type="search" placeholder="업체 코드 또는 회사명 검색" aria-label="협력업체 검색" />

        <div class="company-table-wrap">
          <table class="company-table">
            <thead>
              <tr>
                <th scope="col">
                  <input
                    type="checkbox"
                    aria-label="전체 업체 선택"
                    :checked="companyOptions.length > 0 && companySelectionDraft.length === companyOptions.length"
                    @change="toggleAllCompanies"
                  />
                </th>
                <th scope="col">업체 코드</th>
                <th scope="col">회사 이름</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="company in filteredCompanyOptions" :key="company.CST_CD">
                <td>
                  <input
                    type="checkbox"
                    :aria-label="company.CST_NM + ' 선택'"
                    :checked="companySelectionDraft.includes(company.CST_CD)"
                    @change="toggleCompany(company.CST_CD)"
                  />
                </td>
                <td>{{ company.CST_CD }}</td>
                <td>{{ company.CST_NM }}</td>
              </tr>
              <tr v-if="filteredCompanyOptions.length === 0">
                <td colspan="3" class="company-empty">검색 결과가 없습니다.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="dialog-actions">
          <span class="selection-count">{{ companySelectionDraft.length }}개 업체 선택</span>
          <span class="action-spacer"></span>
          <button type="button" class="secondary-button" @click="closeCompanyDialog">Cancel</button>
          <button type="button" class="primary-button" @click="applyCompanySelection">확인</button>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import axios from 'axios';

const MAX_FILES = 3;
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'pdf'];
const emptyForm = () => ({ title: '', body: '', files: [], selectedCompanies: [] });

export default {
  name: 'NoticePage',
  data() {
    return {
      notices: [],
      listLoading: false,
      listError: '',
      saving: false,
      saveWarning: '',
      isDialogOpen: false,
      isCompanyDialogOpen: false,
      companyOptions: [],
      companyLoading: false,
      companyError: '',
      companySearch: '',
      companySelectionDraft: [],
      editingId: null,
      form: emptyForm(),
      formError: '',
      maxFiles: MAX_FILES
    };
  },
  computed: {
    filteredCompanyOptions() {
      const query = this.companySearch.trim().toLowerCase();
      if (!query) return this.companyOptions;
      return this.companyOptions.filter(company =>
        company.CST_CD.toLowerCase().includes(query) || company.CST_NM.toLowerCase().includes(query)
      );
    },
    selectedCompanyLabel() {
      if (this.companyLoading) return '협력업체 불러오는 중...';
      if (this.companyError) return '협력업체 목록을 확인할 수 없습니다';
      return this.form.selectedCompanies.length === 0
        ? '협력업체 선택'
        : this.recipientLabel(this.form.selectedCompanies);
    }
  },
  methods: {
    async loadNotices() {
      this.listLoading = true;
      this.listError = '';
      try {
        const response = await axios.get('/api/notice');
        if (!Array.isArray(response.data)) throw new Error('Invalid notice list response');
        this.notices = response.data;
      } catch (error) {
        console.error('공지사항 목록 조회 실패:', error);
        this.listError = '공지사항 목록을 불러오지 못했습니다.';
      } finally {
        this.listLoading = false;
      }
    },
    async openAddDialog() {
      this.editingId = null;
      this.form = emptyForm();
      this.formError = '';
      this.isDialogOpen = true;
      await this.loadCompanyOptions();
    },
    async openEditDialog(notice) {
      this.editingId = notice.id;
      this.form = {
        title: notice.title,
        body: notice.body,
        files: [...notice.files],
        selectedCompanies: [...notice.selectedCompanies]
      };
      this.formError = '';
      this.isDialogOpen = true;
      await this.loadCompanyOptions();
    },
    closeDialog() {
      if (this.saving) return;
      this.isDialogOpen = false;
      this.editingId = null;
      this.form = emptyForm();
      this.formError = '';
    },
    async loadCompanyOptions() {
      this.companyLoading = true;
      this.companyError = '';
      this.companyOptions = [];
      try {
        const response = await axios.get('/api/notice/companies');
        if (!Array.isArray(response.data)) {
          throw new Error('Invalid company list response');
        }
        this.companyOptions = response.data
          .filter(company => company && company.CST_CD && company.CST_NM)
          .map(company => ({
            CST_CD: String(company.CST_CD).trim(),
            CST_NM: String(company.CST_NM).trim()
          }));
        if (this.companyOptions.length === 0) {
          this.companyError = '선택할 수 있는 협력업체가 없습니다.';
        }
      } catch (error) {
        console.error('협력업체 목록 조회 실패:', error);
        this.companyError = '협력업체 목록을 불러오지 못했습니다.';
      } finally {
        this.companyLoading = false;
      }
    },
    openCompanyDialog() {
      this.companySelectionDraft = [...this.form.selectedCompanies];
      this.companySearch = '';
      this.isCompanyDialogOpen = true;
    },
    closeCompanyDialog() {
      this.isCompanyDialogOpen = false;
      this.companySelectionDraft = [];
      this.companySearch = '';
    },
    toggleCompany(code) {
      if (this.companySelectionDraft.includes(code)) {
        this.companySelectionDraft = this.companySelectionDraft.filter(item => item !== code);
      } else {
        this.companySelectionDraft.push(code);
      }
    },
    toggleAllCompanies() {
      this.companySelectionDraft = this.companySelectionDraft.length === this.companyOptions.length
        ? []
        : this.companyOptions.map(company => company.CST_CD);
    },
    applyCompanySelection() {
      this.form.selectedCompanies = [...this.companySelectionDraft];
      this.formError = '';
      this.closeCompanyDialog();
    },
    recipientLabel(codes) {
      if (codes.length === 0) return '미선택';
      if (this.companyOptions.length > 0 &&
          codes.length === this.companyOptions.length &&
          this.companyOptions.every(company => codes.includes(company.CST_CD))) return '전체 업체';
      return codes.length + '개 업체';
    },
    onFilesSelected(event) {
      const selectedFiles = Array.from(event.target.files || []);
      event.target.value = '';
      this.formError = '';

      if (this.form.files.length + selectedFiles.length > MAX_FILES) {
        this.formError = '첨부파일은 공지당 최대 3개까지 선택할 수 있습니다.';
        return;
      }
      const oversizedFile = selectedFiles.find(file => file.size > MAX_FILE_SIZE);
      if (oversizedFile) {
        this.formError = oversizedFile.name + ': 파일당 20MB를 초과할 수 없습니다.';
        return;
      }
      const unsupportedFile = selectedFiles.find(file => {
        const extension = file.name.split('.').pop().toLowerCase();
        return !ALLOWED_EXTENSIONS.includes(extension);
      });
      if (unsupportedFile) {
        this.formError = unsupportedFile.name + ': 엑셀, Word, PPT, PDF 파일만 선택할 수 있습니다.';
        return;
      }
      this.form.files.push(...selectedFiles);
    },
    removeFile(index) {
      this.form.files.splice(index, 1);
      this.formError = '';
    },
    async saveNotice() {
      const title = this.form.title.trim();
      const body = this.form.body.trim();
      if (!title || !body) {
        this.formError = '제목과 내용을 입력해 주세요.';
        return;
      }
      if (this.companyLoading || this.companyError) {
        this.formError = '협력업체 목록을 불러온 뒤 다시 시도해 주세요.';
        return;
      }
      if (this.form.selectedCompanies.length === 0) {
        this.formError = '협력업체를 1곳 이상 선택해 주세요.';
        return;
      }
      const data = new FormData();
      data.append('title', title);
      data.append('body', body);
      data.append('selectedCompanies', JSON.stringify(this.form.selectedCompanies));
      if (this.editingId !== null) {
        data.append('keepBlobNames', JSON.stringify(this.form.files.filter(file => file.blobName).map(file => file.blobName)));
      }
      for (const file of this.form.files.filter(file => !file.blobName)) data.append('files', file);
      this.saving = true;
      this.formError = '';
      this.saveWarning = '';
      try {
        const response = this.editingId === null
          ? await axios.post('/api/notice', data)
          : await axios.put(`/api/notice/${this.editingId}`, data);
        this.saving = false;
        this.closeDialog();
        await this.loadNotices();
        if (response.data.cleanupFailed) {
          this.saveWarning = '공지는 저장됐지만 제거한 파일의 Azure 삭제에 실패했습니다. 관리자에게 확인을 요청해 주세요.';
        }
      } catch (error) {
        console.error('공지사항 저장 실패:', error);
        this.formError = error.response?.status === 400
          ? '입력값 또는 첨부파일을 확인해 주세요.'
          : '공지사항을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.';
      } finally {
        this.saving = false;
      }
    },
    async deleteNotice() {
      if (!window.confirm('이 공지를 삭제하시겠습니까?')) return;
      this.saving = true;
      this.formError = '';
      try {
        await axios.delete(`/api/notice/${this.editingId}`);
        this.saving = false;
        this.closeDialog();
        await this.loadNotices();
      } catch (error) {
        console.error('공지사항 삭제 실패:', error);
        this.saving = false;
        this.closeDialog();
        await this.loadNotices();
        this.saveWarning = '공지 또는 Azure 파일 삭제를 완료하지 못했습니다. 목록에서 상태를 확인한 뒤 다시 시도해 주세요.';
      } finally {
        this.saving = false;
      }
    },
    async retryDelete(notice) {
      if (this.saving) return;
      this.saving = true;
      this.saveWarning = '';
      try {
        await axios.delete(`/api/notice/${notice.id}`);
        await this.loadNotices();
      } catch (error) {
        console.error('Azure 파일 삭제 재시도 실패:', error);
        this.saveWarning = 'Azure 파일 삭제를 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.';
      } finally {
        this.saving = false;
      }
    },
    attachmentUrl(file) {
      return `/api/notice/${this.editingId}/file?blobName=${encodeURIComponent(file.blobName)}`;
    },
    formatFileSize(bytes) {
      return (bytes / 1024 / 1024).toFixed(1) + 'MB';
    },
    formatDate(date) {
      const value = String(date || '');
      if (/^\d{8}$/.test(value)) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}`;
      return new Date(date).toLocaleString('ko-KR');
    }
  },
  mounted() {
    this.loadNotices();
  }
};
</script>

<style scoped>
.notice-page { max-width: 1100px; margin: 32px auto; padding: 0 24px 40px; }
.back-link { color: #436591; text-decoration: none; }
.back-link:hover { text-decoration: underline; }
.page-header, .panel-header, .dialog-header, .attachments-heading, .dialog-actions {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
}
.page-header { margin: 22px 0 24px; }
.page-header h1 { margin: 0 0 4px; font-size: 28px; font-weight: 700; }
.page-header p { margin: 0; color: #64748b; }
.primary-button, .secondary-button, .delete-button {
  border: 0; border-radius: 8px; padding: 10px 18px; font: inherit; font-weight: 600; cursor: pointer;
}
.primary-button { background: #1769c2; color: white; }
.primary-button:hover { background: #10549e; }
.secondary-button { background: #edf1f5; color: #334155; }
.delete-button { background: #fff1f0; color: #b42318; }
.notice-panel { background: white; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
.panel-header { padding: 20px 24px; border-bottom: 1px solid #e2e8f0; }
.panel-header h2 { margin: 0; font-size: 18px; font-weight: 700; }
.panel-header span, .preview-note { color: #64748b; font-size: 13px; }
.empty-state { padding: 60px 24px; color: #64748b; text-align: center; }
.notice-item {
  display: flex; flex-direction: column; width: 100%; padding: 17px 24px;
  border: 0; border-bottom: 1px solid #eef1f4; background: white; text-align: left; cursor: pointer;
}
.notice-item:last-child { border-bottom: 0; }
.notice-item:hover, .notice-item:focus-visible { background: #f5f9ff; }
.notice-item-title { color: #1e293b; font-size: 16px; font-weight: 700; }
.notice-item-body { margin-top: 5px; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.notice-item-meta { margin-top: 8px; color: #64748b; font-size: 12px; }
.preview-note { margin-top: 14px; }
.dialog-overlay {
  position: fixed; inset: 0; z-index: 1000; display: flex; justify-content: center;
  align-items: center; padding: 20px; background: rgba(15, 23, 42, .55);
}
.notice-dialog {
  width: min(100%, 680px); max-height: 90vh; overflow-y: auto; padding: 24px;
  border-radius: 12px; background: white; box-shadow: 0 20px 60px rgba(0, 0, 0, .2);
}
.dialog-header { margin-bottom: 22px; }
.dialog-header h2 { margin: 0; font-size: 22px; font-weight: 700; }
.close-button { border: 0; background: none; color: #64748b; font-size: 28px; cursor: pointer; }
.field-label { display: block; margin: 18px 0 7px; font-weight: 600; }
.text-input { width: 100%; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font: inherit; }
.notice-content-fields { margin-top: 24px; border: 1px solid #dbe5ef; border-radius: 10px; overflow: hidden; }
.notice-content-field { padding: 16px 18px; }
.notice-content-field + .notice-content-field { border-top: 1px solid #dbe5ef; background: #fbfdff; }
.notice-content-field .field-label { margin: 0 0 9px; color: #1e293b; }
.text-input:focus, .file-input:focus-visible, .company-select-button:focus-visible {
  outline: 2px solid #90bcef; outline-offset: 1px;
}
.company-select-button {
  display: block; width: 100%; padding: 11px 12px; border: 1px solid #cbd5e1;
  border-radius: 8px; background: white; color: #334155; font: inherit;
  text-align: left; cursor: pointer;
}
.company-select-button:disabled { color: #64748b; background: #f8fafc; cursor: not-allowed; }
.company-select-button::after { content: '▾'; float: right; color: #64748b; }
.retry-button { border: 0; background: none; color: #1769c2; font: inherit; text-decoration: underline; cursor: pointer; }
.company-overlay { z-index: 1100; }
.company-dialog {
  width: min(100%, 800px); max-height: 90vh; overflow-y: auto; padding: 24px;
  border-radius: 12px; background: white; box-shadow: 0 20px 60px rgba(0, 0, 0, .2);
}
.company-dialog .preview-note { margin: -8px 0 16px; }
.company-search { margin-bottom: 16px; }
.company-table-wrap { max-height: 400px; overflow: auto; border: 1px solid #e2e8f0; border-radius: 8px; }
.company-table { width: 100%; border-collapse: collapse; }
.company-table th, .company-table td { padding: 11px 14px; border-bottom: 1px solid #e2e8f0; text-align: left; }
.company-table th { position: sticky; top: 0; background: #f6f9fc; font-weight: 600; }
.company-table th:first-child, .company-table td:first-child { width: 56px; text-align: center; }
.company-table tbody tr:last-child td { border-bottom: 0; }
.company-table tbody tr:hover { background: #f5f9ff; }
.company-empty { color: #64748b; text-align: center !important; }
.selection-count { color: #475569; font-size: 13px; }
.body-input { min-height: 160px; resize: vertical; }
.attachments-heading .field-label { margin-bottom: 5px; }
.attachments-heading span, .file-help { color: #64748b; font-size: 13px; }
.file-help { margin: 0 0 10px; }
.file-input { width: 100%; padding: 10px; border: 1px dashed #94a3b8; border-radius: 8px; }
.file-list { list-style: none; margin: 12px 0 0; padding: 0; }
.file-list li {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 9px 0; border-bottom: 1px solid #eef1f4;
}
.file-name { overflow-wrap: anywhere; }
.file-name small { color: #64748b; }
.remove-file-button { flex: none; border: 0; background: none; color: #b42318; cursor: pointer; }
.form-error { margin: 14px 0 0; color: #b42318; font-size: 13px; }
.dialog-actions { margin-top: 28px; justify-content: flex-end; }
.action-spacer { flex: 1; }
@media (max-width: 600px) {
  .notice-page { padding: 0 16px 32px; }
  .page-header { align-items: flex-start; }
  .page-header h1 { font-size: 24px; }
  .notice-dialog { padding: 18px; }
  .company-dialog { padding: 18px; }
}
</style>
